import {
    AppLayout,
    Banner,
    BBUIApp,
    Button,
    Footer,
    Modal,
    TitleBar,
    useNotification,
} from "@bbnpm/bb-ui-framework";
import Popout from "@bloomberg/web-icons-react/Popout";
import QuestionCircle from "@bloomberg/web-icons-react/QuestionCircle";
import User from "@bloomberg/web-icons-react/User";
import { useEffect, useState } from "react";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { Route, Switch, useLocation } from "react-router-dom";
import Downloads from "../../downloads/downloads-entry";
import Error from "../../error/error-entry";
import Help from "../../help/help-entry";
import Landing from "../../landing/landing-entry";
import Launch from "../../launch/launch-entry";
import Loading from "../../loading/loading-entry";
import { MigrationPhases, Settings } from "../../settings";
import "./App.min.css";
import ErrorBoundary from "./ErrorBoundary";

const App = () => {
    const SITE_ADDRESS = process.env.PUBLIC_URL + "/";
    const APPWIDTH = 1280,
        location = useLocation();
    const htmlTitle = sanitizeHTML(window.SETTINGS.bannerErrorTitle);
    const htmlDetails = sanitizeHTML(window.SETTINGS.bannerErrorDetails);
    const [citrixPopup, setCitrixPopup] = useState(true);
    const [migrationBannerOpen, setMigrationBannerOpen] = useState(true);
    const popUpMessage = window.SETTINGS.popUp;
    const notif = useNotification();
    const [hasServerError, setHasServerError] = useState(
        htmlTitle.length > 0 || htmlDetails.length > 0,
    );

    // Scoped to the launch page because migration phase is a property of the signed-in
    // user, and suppressed when the backend already supplies a banner so the two
    // cannot stack into an 80px header.
    const showMigrationBanner =
        migrationBannerOpen &&
        location.pathname === SITE_ADDRESS + "launch" &&
        Settings.migration.phase === MigrationPhases.OptIn &&
        window.SETTINGS.bannerMessage.length === 0;

    function sanitizeHTML(backendHTML) {
        return backendHTML
            .replaceAll("&amp;", "&")
            .replaceAll("&lt;", "<")
            .replaceAll("&gt;", ">")
            .replaceAll("&#39;", "'")
            .replaceAll("&#34;", "'");
    }

    useEffect(() => {
        if (!hasServerError) {
            notif.clearAll();
            return;
        }

        const addErrorNotification = new Promise((resolve, reject) => {
            // No banner notification if we are on the error page
            if (htmlTitle && window.location.pathname !== "/error") {
                if (htmlDetails) {
                    notif.addError({
                        message: htmlTitle,
                        details: (
                            <div className="bbui-notification-details-content">
                                {htmlDetails && (
                                    <span dangerouslySetInnerHTML={{ __html: htmlDetails }}></span>
                                )}
                            </div>
                        ),
                    });
                } else {
                    notif.addError({
                        message: htmlTitle,
                    });
                }
                resolve();
            }
            reject();
        });

        // default to open error notification
        addErrorNotification
            .then(() => {
                let message = document.querySelector(
                    "#bbui-notification-container .bbui-notification-message",
                );
                if (message) message.ariaExpanded = true;

                let dropdown = document.querySelector(
                    "#bbui-notification-container #bbui-notification-error-dropdown",
                );
                if (dropdown) {
                    dropdown.ariaHidden = false;
                    setTimeout(() => (dropdown.style.maxHeight = "none"), 100);
                }

                return message;
            })
            .then((message) => {
                if (message) message.click(); // toggle default closed state
            })
            .catch(() => {});
    }, [htmlDetails, htmlTitle, hasServerError, notif]);

    return (
        // "panel" not "sticky": sticky breaks the help/downloads section navs.
        <BBUIApp activeTheme="dark" kind="panel">
            <AppLayout.Header>
                {showMigrationBanner && (
                    <Banner
                        kind="info"
                        width={APPWIDTH + "px"}
                        message="A New Client is Here: Parallels Client will be the default way to access the Terminal."
                        link={
                            <a href={SITE_ADDRESS + "downloads"} target="_blank" rel="noreferrer">
                                Learn More
                            </a>
                        }
                        onClose={() => setMigrationBannerOpen(false)}
                    />
                )}

                {window.SETTINGS.bannerMessage.length > 0 &&
                    window.SETTINGS.bannerKind.length > 0 && (
                        <Banner
                            kind={window.SETTINGS.bannerKind}
                            message={window.SETTINGS.bannerMessage}
                            link={
                                window.SETTINGS.bannerLinkText.length > 0 &&
                                window.SETTINGS.bannerLinkHref.length > 0 ? (
                                    <a href={window.SETTINGS.bannerLinkHref}>
                                        {window.SETTINGS.bannerLinkText}
                                    </a>
                                ) : undefined
                            }
                        />
                    )}

                {location.pathname === "/" || location.pathname === "/client" ? (
                    <div className="above-title">
                        <ul>
                            <li>
                                <a
                                    href="https://www.bloomberg.com/company/"
                                    target="_blank"
                                    rel="noreferrer"
                                    tabIndex="-1"
                                >
                                    Bloomberg the Company & its Products{" "}
                                    <Popout className="popout-icon" />
                                </a>
                            </li>
                            {location.pathname === "/" ? (
                                <li>
                                    <a
                                        href="https://www.bloomberg.com/professional/contact-menu/?utm_source=bbgmenu-demo&bbgsum=DG-WS-CORE-bbgmenu-demo"
                                        target="_blank"
                                        rel="noreferrer"
                                        tabIndex="-1"
                                    >
                                        Bloomberg Terminal Demo Request{" "}
                                        <Popout className="popout-icon" />
                                    </a>
                                </li>
                            ) : (
                                ""
                            )}
                        </ul>
                    </div>
                ) : (
                    ""
                )}

                <TitleBar
                    productName="ANYWHERE"
                    skipToId="main-content"
                    brandRenderer={({ children, ...rest }) => (
                        <a href={SITE_ADDRESS} {...rest}>
                            {children}
                        </a>
                    )}
                    envIndicator={
                        window.SETTINGS.env.length > 0 && (
                            <span style={{ color: "cyan" }}>{window.SETTINGS.env}</span>
                        )
                    }
                    width={APPWIDTH + "px"}
                >
                    <TitleBar.Links>
                        {location.pathname !== SITE_ADDRESS + "help" && (
                            <TitleBar.IconLink
                                icon={<QuestionCircle />}
                                text="Help"
                                onClick={() =>
                                    window.open(SITE_ADDRESS + "help", "_blank")?.focus()
                                }
                            />
                        )}

                        {location.pathname === SITE_ADDRESS + "launch" && (
                            <TitleBar.IconLink
                                icon={<User />}
                                text="User"
                                menuItems={[
                                    {
                                        label: "Logout",
                                        renderer: "a",
                                        to: SITE_ADDRESS + "logout",
                                        href: SITE_ADDRESS + "logout",
                                    },
                                ]}
                            />
                        )}
                    </TitleBar.Links>
                </TitleBar>
            </AppLayout.Header>

            <Modal
                id="citrixModal"
                isOpen={popUpMessage !== "" && popUpMessage !== null && citrixPopup}
                portalContainerId="root"
                primaryActions={
                    <>
                        <Button kind="secondary" onClick={() => setCitrixPopup(false)}>
                            Ok
                        </Button>
                    </>
                }
            >
                {popUpMessage && <Markdown remarkPlugins={[remarkGfm]}>{popUpMessage}</Markdown>}
            </Modal>

            <AppLayout.Main className="main-content" id="main-content">
                <Switch>
                    <Route exact path={SITE_ADDRESS + ""}>
                        <Landing />
                    </Route>

                    <Route path={SITE_ADDRESS + "loading"}>
                        <Loading />
                    </Route>
                    <Route path={SITE_ADDRESS + "error"}>
                        <Error />
                    </Route>
                    <Route path={SITE_ADDRESS + "notfound"}>
                        <Error />
                    </Route>
                    <Route path={SITE_ADDRESS + "help"}>
                        <Help />
                    </Route>
                    <Route path={SITE_ADDRESS + "downloads"}>
                        <Downloads />
                    </Route>
                    <Route path={SITE_ADDRESS + "launch"}>
                        <ErrorBoundary fallback={<Error />}>
                            <Launch setHasServerError={setHasServerError} />
                        </ErrorBoundary>
                    </Route>

                    {/* Catch-all, must stay last: anything below is unreachable. */}
                    <Route>
                        <Error />
                    </Route>
                </Switch>
            </AppLayout.Main>

            <AppLayout.Footer>
                <Footer copyright={new Date().getFullYear()} width={APPWIDTH + "px"}>
                    <Footer.Links>
                        <Footer.Link>
                            <a href="https://www.bloomberg.com/feedback/">Contact Us</a>
                        </Footer.Link>
                        <Footer.Link>
                            <a href="https://www.bloomberg.com/notices/tos/">
                                Terms and Conditions of Use
                            </a>
                        </Footer.Link>
                        <Footer.Link>
                            <a href="https://www.bloomberg.com/notices/trademarks/">Trademarks</a>
                        </Footer.Link>
                        <Footer.Link>
                            <a href="https://www.bloomberg.com/notices/privacy/">Privacy Policy</a>
                        </Footer.Link>
                    </Footer.Links>
                </Footer>
            </AppLayout.Footer>
        </BBUIApp>
    );
};

export default App;
